import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defence: a render error anywhere below this becomes a page the
 * user can recover from rather than a blank screen.
 *
 * Still a class component because React has no hook equivalent for
 * componentDidCatch.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Unhandled render error", error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="space-y-2">
          <h1 className="text-xl font-semibold">Algo deu errado</h1>
          <p className="text-muted-foreground max-w-md text-sm">
            Não foi possível exibir a página. Recarregar costuma resolver.
          </p>
        </div>
        <Button onClick={() => window.location.reload()}>Recarregar a página</Button>
      </div>
    );
  }
}
